import * as React from 'react'
import { unstable_usePrompt, useLocation } from 'react-router-dom'
import { useBeforeUnload, useMount } from 'react-use'
import {
  canAccessAdminFeature,
  canManageAdminFeature,
  firstAccessibleAdminPath,
  isMainAdministratorProfile,
  type AdminFeature,
} from 'src/helpers/adminAccess'
import env from 'src/helpers/env'
import { useAuth } from 'src/hooks/auth/authObservableValue'
import { useTranslatable } from 'src/hooks/locale/utils'
import useSWR, { type Fetcher, type SWRConfiguration, type SWRResponse } from 'swr'

export interface APIResponse<Data, Error> extends SWRResponse<Data, Error> {
  readonly data: Data
}
interface FetchingConfig {
  readonly shouldFetch?: boolean | null | undefined
  readonly suspense?: boolean | null | undefined
}

interface UseApiConfig<Data, P, E extends (p: P) => Promise<Data>> {
  readonly endpoint: E
  readonly params: P
  readonly shouldFetch?: boolean | null | undefined
}

export function useApi<P, Data, C extends FetchingConfig, Error>(
  config: C & UseApiConfig<Data, P, (p: P) => Promise<Data>> & SWRConfiguration<Data, Error, Fetcher<Data, unknown[]>>
): APIResponse<
  C extends
    | {
        readonly shouldFetch: boolean | null | undefined
      }
    | {
        readonly suspense: boolean | null | undefined
      }
    ? Data | null
    : Data,
  Error
> {
  async function fn(): Promise<Data> {
    return await config.endpoint(config.params)
  }
  // @ts-expect-error TODO: fix this
  return useSWR((config.shouldFetch ?? true) ? [config.endpoint, config.params] : null, fn, config) as APIResponse<
    C extends
      | {
          readonly shouldFetch: boolean | null | undefined
        }
      | {
          readonly suspense: boolean | null | undefined
        }
      ? Data | null
      : Data,
    Error
  >
}

function useOutsideClick(elRef: React.RefObject<Element>, fn: () => unknown): void {
  const fnRef = React.useRef(fn)

  fnRef.current = fn

  useMount(() => {
    function handleClickOutside(e: MouseEvent): void {
      if (e.target == null) return void null
      if (elRef.current == null) return void null
      if (elRef.current.contains(e.target as Node)) return void null

      fnRef.current()
    }

    document.addEventListener('mouseup', handleClickOutside)

    return () => void document.removeEventListener('mouseup', handleClickOutside)
  })
}

export interface DropdownProps<T extends Element> {
  readonly show: boolean
  readonly setShow: (arg0: boolean) => void
  readonly ref: React.RefObject<T>
  readonly id: string
}

export function useDropdown<T extends Element>(hideOnOutsideClick: boolean = true): DropdownProps<T> {
  const [show, setShow] = React.useState<boolean>(false)
  const ref = React.useRef<T>(null)
  const id = `label-${React.useId()}`

  useOutsideClick(ref, () => {
    if (hideOnOutsideClick) {
      setShow(false)
    }
  })

  return {
    show,
    setShow,
    ref,
    id,
  }
}

export function useConsoleWarning(): void {
  const t = useTranslatable()

  React.useEffect(() => {
    if (env.APP_ENV === 'PRODUCTION') {
      const warningTitleCSS = 'color:red; font-size:60px; font-weight: bold; -webkit-text-stroke: 1px black;'
      const warningDescCSS = 'font-size: 18px;'
      // eslint-disable-next-line no-restricted-properties
      console.log(`%c${t('error:stop')}`, warningTitleCSS)
      // eslint-disable-next-line no-restricted-properties
      console.log(`%c${t('error:browser_warning')}`, warningDescCSS)
    }
  }, [t])
}

export function useFormikUnload({
  when,
  message = 'You have unsaved changes, are you sure?',
}: {
  when: boolean
  message?: string
}): void {
  useBeforeUnload(when, message)
  unstable_usePrompt({ when, message })
}

export function useScrollReset(ref: React.RefObject<HTMLDivElement>): void {
  const { pathname } = useLocation()
  React.useEffect(() => {
    ref.current?.scrollIntoView({
      block: 'end',
      inline: 'end',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])
}

export interface AdminAccess {
  /** Menu item, tab or route may be shown/opened. */
  readonly can: (feature: AdminFeature) => boolean
  /** Save / add / edit / delete controls may be shown. */
  readonly canManage: (feature: AdminFeature) => boolean
  readonly isMainAdministrator: boolean
  /** Where the admin home page and denied routes land; `null` when nothing is accessible. */
  readonly firstAccessiblePath: string | null
}

export function useAdminAccess(): AdminAccess {
  const { profile } = useAuth()
  return React.useMemo(
    () => ({
      can: (feature: AdminFeature) => canAccessAdminFeature(profile, feature),
      canManage: (feature: AdminFeature) => canManageAdminFeature(profile, feature),
      isMainAdministrator: isMainAdministratorProfile(profile),
      firstAccessiblePath: firstAccessibleAdminPath(profile),
    }),
    [profile]
  )
}

export function createBarChartOptions(
  data: { categories?: readonly string[]; series?: readonly number[] } | null | undefined,
  title: string,
  isDarkMode: boolean
): Highcharts.Options {
  const baseColors = {
    light: '#000000',
    dark: '#ffffff',
    label: '#cccccc',
    grid: '#444444',
  }

  const color = isDarkMode ? baseColors.dark : baseColors.light
  const labelColor = isDarkMode ? baseColors.label : baseColors.light
  const gridColor = isDarkMode ? baseColors.grid : undefined

  return {
    chart: {
      type: 'bar',
      backgroundColor: 'transparent',
      spacingBottom: 20,
      height: 600,
    },
    credits: { enabled: false },
    title: {
      text: title,
      style: { color },
    },
    yAxis: {
      title: { text: undefined, style: { color } },
      ...(isDarkMode && {
        labels: { style: { color: labelColor } },
        gridLineColor: gridColor,
      }),
    },
    xAxis: {
      type: 'category',
      categories: Array.from(data?.categories ?? []),
      ...(isDarkMode && {
        labels: { style: { color: labelColor } },
        gridLineColor: gridColor,
      }),
    },
    plotOptions: {
      bar: { cursor: 'pointer' },
    },
    series: [
      {
        type: 'bar',
        data: Array.from(data?.series ?? []),
        dataLabels: {
          enabled: true,
          format: '<b>{point.y}</b>',
          style: { color },
        },
      },
    ],
    legend: { enabled: false },
    responsive: {
      rules: [
        {
          condition: { maxWidth: 500 },
          chartOptions: {
            plotOptions: {
              pie: {
                size: '70%',
                dataLabels: { enabled: false },
              },
            },
          },
        },
      ],
    },
  }
}

export function useDebouncedLoadOptions<T>(
  fetcher: (query: string) => Promise<readonly T[]>,
  options?: { debounceMs?: number; skipEmpty?: boolean }
): (inputValue: string) => Promise<readonly T[]> {
  const debounceMs = options?.debounceMs ?? 300
  const skipEmpty = options?.skipEmpty ?? false
  const seqRef = React.useRef(0)
  const fetcherRef = React.useRef(fetcher)
  fetcherRef.current = fetcher

  return React.useCallback(
    (inputValue: string): Promise<readonly T[]> =>
      new Promise((resolve) => {
        const q = inputValue.trim()
        if (skipEmpty && q.length === 0) {
          seqRef.current++
          resolve([])
          return
        }
        const seq = ++seqRef.current
        window.setTimeout(() => {
          void (async () => {
            if (seq !== seqRef.current) {
              resolve([])
              return
            }
            try {
              const result = await fetcherRef.current(q)
              if (seq !== seqRef.current) {
                resolve([])
                return
              }
              resolve(result)
            } catch {
              resolve([])
            }
          })()
        }, debounceMs)
      }),
    [debounceMs, skipEmpty]
  )
}
